import { Tooltip } from "@mui/material";
import { useProfile } from "contexts/ProfileContext.tsx";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import { DtoUsuarioRegrasString } from "models/DtoUsuarioRegrasString.ts";
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation } from "react-router-dom";
import { List, RowComponentProps } from "react-window";
import Empty from "../../assets/lotties/empty.json";
import {
	currencyFields,
	formatCellValue,
	percentualFields,
} from "../../pages/ModuloGerenciador/Embarque/components/helpers/TabelaEmbarqueVirtualizadaHelpers.tsx";
import LoadingComponent from "../LoadingComponent/LoadingComponent.tsx";
import "./TabelaVirtualizadaGenerica.css";

const FIELD_WIDTHS: Record<string, { min: number; max: number }> = {
	cte: { min: 80, max: 250 },
	freteMotoristaGerenciador: { min: 80, max: 250 },
	freteMotoristaSat: { min: 80, max: 250 },
	freteFiscalGerenciador: { min: 80, max: 250 },
	freteFiscalSat: { min: 80, max: 250 },
	pedagioGerenciador: { min: 80, max: 250 },
	pedagioSat: { min: 150, max: 250 },
	icmsGerenciador: { min: 80, max: 250 },
	icmsSat: { min: 150, max: 250 },
	usuarioResponsavel: { min: 150, max: 250 },
	temDiferenca: { min: 80, max: 250 },
	dataUltimaVerificacao: { min: 80, max: 250 },
	placaCarreta: { min: 80, max: 100 },
	placaCavalo: { min: 80, max: 100 },
	placaCarreta2: { min: 80, max: 100 },
	tipoVeiculo: { min: 80, max: 130 },
	tipoVeiculoConjuntos: { min: 80, max: 100 },
	unidadeEmbarque: { min: 80, max: 100 },
	numeroEixos: { min: 40, max: 56 },
	motorista: { min: 80, max: 130 },
	motorista2: { min: 80, max: 130 },
	tipoCarroceria: { min: 80, max: 130 },
	proprietario: { min: 80, max: 130 },
	dataUltimoCarregamento: { min: 150, max: 250 },
	dataProximaViagemNoPeriodo: { min: 200, max: 250 },
	dataProximaViagemAgendadaNoPeriodo: { min: 200, max: 340 },
	tipoRota: { min: 190, max: 190 },
	endereco: { min: 340, max: 340 },
	km: { min: 70, max: 120 },
	permissoes: { min: 600, max: 600 },
	enderecoMotorista: { min: 150, max: 150 },
	telefoneMotorista: { min: 100, max: 120 },
	faturamento: { min: 100, max: 150 },
	ticketMedio: { min: 100, max: 120 },
	restituicao: { min: 100, max: 100 },
	impostoFederal: { min: 100, max: 150 },
	custoAdm: { min: 100, max: 150 },
	faturamentoAnoAnterior: { min: 100, max: 120 },
	faturamentoAnoSelecionado: { min: 100, max: 120 },
	cliente: { min: 100, max: 90 },
	unidade: { min: 100, max: 90 },
	percentualSobreFaturamento: { min: 100, max: 170 },
	quantidadeViagens: { min: 100, max: 110 },
	crescimentoAA: { min: 100, max: 150 },
	pedido: { min: 80, max: 120 },
	dia: { min: 40, max: 60 },
	meta: { min: 150, max: 220 },
	realizado: { min: 150, max: 220 },
	porcentualAtingido: { min: 100, max: 300 },
};

const tooltipFieldNames = [
	"rota",
	"tipoRota",
	"permissoes",
	"destinatario",
	"embarcador",
	"cliente",
	"endereco",
	"motorista",
	"motorista2",
	"enderecoMotorista",
];

export type ColunaConfig<T> = {
	id?: string;
	field?: keyof T | string;
	campo?: string;
	nome?: string;
	symbol?: symbol;
	header?: string;
	minWidth?: number;
	maxWidth?: number;
	tipo?: "text" | "number" | "date" | string;
	nomeColuna?: string;
	render?: (row: T, index: number) => React.ReactNode;
	objetoEndereco?: string;
};

type TabelaVirtualizadaGenericaProps<T> = {
	dados: T[];
	colunas: ColunaConfig<T>[];
	height?: number;
	rowHeight: number | ((index: number) => number);
	getRowClassName?: (row: T, index: number) => string | undefined;
	getCellClassName?: (
		coluna: ColunaConfig<T>,
		row: T,
		rowIndex: number,
	) => string | undefined;
	renderCell?: (
		coluna: ColunaConfig<T>,
		row: T,
		rowIndex: number,
	) => React.ReactNode;
	onRowClick?: (row: T, index: number) => void;
	onHeaderClick?: (column: string | number | symbol) => void;
	columnWidthsOverride?: boolean;
	centralized?: boolean;
	isLoading?: boolean;
	hasFooter?: boolean;
	fullHeight?: boolean;
};

type ExtraRowProps<T> = {
	dados: T[];
	colunas: ColunaConfig<T>[];
	gridTemplateColumns: string;
	columnWidths: number[];
	getRowClassName?: (row: T, index: number) => string | undefined;
	getCellClassName?: (
		coluna: ColunaConfig<T>,
		row: T,
		rowIndex: number,
	) => string | undefined;
	renderCell?: (
		coluna: ColunaConfig<T>,
		row: T,
		rowIndex: number,
	) => React.ReactNode;
	onRowClick?: (row: T, index: number) => void;
};

function getDefaultCellClasses<T>(coluna: ColunaConfig<T>, row: T): string {
	const field = String(coluna?.field ?? "");
	const classes: string[] = [];

	if (percentualFields.includes(field)) {
		classes.push("td-right-no-padding");
	}
	if (currencyFields.includes(field)) {
		classes.push("td-right");
	}

	return classes.filter(Boolean).join(" ");
}

function calcularLargurasColunas<T>(
	rows: T[],
	colunas: ColunaConfig<T>[],
): number[] {
	if (!rows?.length || !colunas?.length) return colunas?.map(() => 80);

	const pxPorCaractere = 8;
	const paddingHorizontal = 24;

	return colunas.map((coluna) => {
		const field = String(coluna.field ?? coluna.id ?? "");
		const { min = 40, max = 150 } = FIELD_WIDTHS[field] || {};

		const headerText = String(coluna.header ?? coluna.field ?? coluna.id ?? "");
		let maxChars = headerText.length;

		for (const row of rows) {
			const rawValue =
				field && (row as any)[field] != null ? (row as any)[field] : "";
			const text = String(rawValue);
			if (text.length > maxChars) {
				maxChars = text.length;
			}
		}

		const baseWidth = maxChars * pxPorCaractere + paddingHorizontal;

		return Math.max(min, Math.min(baseWidth, max));
	});
}

function GenericRow<T>({
	index,
	style,
	dados,
	colunas,
	gridTemplateColumns,
	columnWidths,
	getRowClassName,
	getCellClassName,
	renderCell,
	onRowClick,
}: RowComponentProps<ExtraRowProps<T>>) {
	const row = dados[index];

	const isStriped = index % 2 === 1;

	const baseRowClass = "linha-tabela";
	const stripedClass = isStriped ? "linha-tabela--striped" : "";
	const extraRowClass = getRowClassName?.(row, index);

	const rowClasses = [baseRowClass, stripedClass, extraRowClass]
		.filter(Boolean)
		.join(" ");

	const handleClick = useCallback(() => {
		onRowClick?.(row, index);
	}, [onRowClick, row, index]);

	return (
		<div
			style={{
				...style,
				display: "grid",
				gridTemplateColumns,
				width: columnWidths.reduce((s, w) => s + w, 0),
			}}
			className={rowClasses}
			onClick={handleClick}
		>
			{colunas.map((coluna, colIndex) => {
				const key = (coluna.field ?? coluna.id ?? `col-${colIndex}`) as keyof T;

				let content =
					renderCell?.(coluna, row, index) ??
					(coluna.render
						? coluna.render(row, index)
						: formatCellValue({
								value: key ? (row as any)[key] : null,
								configDataHora: true,
								field: coluna.field,
							}));

				const baseCellClass = "tabela-generica-cell";
				const extraCellClass =
					getCellClassName?.(coluna, row, index) ||
					getDefaultCellClasses(coluna, row);
				const cellClasses = [baseCellClass, extraCellClass]
					.filter(Boolean)
					.join(" ");
				const fieldName = String(coluna?.field ?? "");

				if (key === "permissoes") {
					content = (row as DtoUsuarioRegrasString).regras.join(", ");
				}
				const isPrimitiveContent =
					typeof content === "string" || typeof content === "number";

				if (tooltipFieldNames.includes(fieldName) && isPrimitiveContent) {
					return (
						<Tooltip placement="top" title={String(content)} key={String(key)}>
							<div
								key={String(key)}
								className={cellClasses}
								style={{ width: columnWidths[colIndex] }}
							>
								{content}
							</div>
						</Tooltip>
					);
				}

				return (
					<div
						key={String(key)}
						className={cellClasses}
						style={{ width: columnWidths[colIndex] }}
					>
						{content}
					</div>
				);
			})}
		</div>
	);
}

const HeaderRow = <T,>({
	colunas,
	gridTemplateColumns,
	totalGridWidth,
	onHeaderClick,
}: {
	colunas: ColunaConfig<T>[];
	gridTemplateColumns: string;
	totalGridWidth: number;
	onHeaderClick?: (column: string | number | symbol) => void;
}) => {
	const { tabelaEmpresa } = useProfile();

	const quebraColunaPedido = useMemo(() => {
		return tabelaEmpresa.Pedido.find((item) => item.quebraColunaPedido);
	}, [tabelaEmpresa.Pedido]);

	return (
		<div
			className="tabela-generica-header"
			style={{
				display: "grid",
				gridTemplateColumns,
				width: totalGridWidth,
			}}
		>
			{colunas.map((coluna, index) => {
				const headerLabel =
					coluna.header ?? String(coluna.nomeColuna ?? coluna.id ?? "");

				const headsplit: string[] | undefined = headerLabel?.split(" ");
				const shouldBreakLine =
					quebraColunaPedido && (headsplit?.length || 0) >= 2;

				const label = shouldBreakLine ? (
					<>
						{headsplit!.slice(0, Math.ceil(headsplit!.length / 2)).join(" ")}
						<br />
						{headsplit!.slice(Math.ceil(headsplit!.length / 2)).join(" ")}
					</>
				) : (
					headerLabel
				);

				return (
					<Tooltip placement="top" title={label} key={headerLabel}>
						<div
							key={String(label) + index}
							className="tabela-generica-header-cell"
							style={{
								cursor: onHeaderClick ? "pointer" : "default",
							}}
							onClick={() =>
								onHeaderClick?.(
									(coluna.field || coluna.campo || coluna.nome) ?? "",
								)
							}
						>
							{label}
						</div>
					</Tooltip>
				);
			})}
		</div>
	);
};

export function TabelaVirtualizadaGenerica<T>({
	dados,
	colunas,
	rowHeight,
	getRowClassName,
	getCellClassName,
	renderCell,
	onRowClick,
	onHeaderClick,
	columnWidthsOverride,
	centralized,
	isLoading = false,
	hasFooter = true,
	fullHeight = false,
}: TabelaVirtualizadaGenericaProps<T>) {
	const location = useLocation();

	const itemCount = useMemo(() => dados?.length ?? 0, [dados?.length]);

	const targetContentKey = useMemo(() => {
		if (isLoading) return "loading";
		if (itemCount === 0) return "empty";
		return "loaded";
	}, [isLoading, itemCount]);

	const [contentKey, setContentKey] = useState<string>(targetContentKey);
	const [tabelaHeight, setTabelaHeight] = useState(600);
	const [centralizedByWidth, setCentralizedByWidth] = useState(centralized);
	const [wrapperEl, setWrapperEl] = useState<HTMLDivElement | null>(null);
	const [tableEl, setTableEl] = useState<HTMLDivElement | null>(null);

	const wrapperRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const columnWidths = useMemo(() => {
		if (columnWidthsOverride) {
			return colunas.map((coluna) => {
				const minWidth = coluna.minWidth ?? 40;
				return Math.max(minWidth, 40);
			});
		}

		return calcularLargurasColunas<T>(dados, colunas);
	}, [dados, colunas, columnWidthsOverride]);

	const totalGridWidth = useMemo(
		() => columnWidths.reduce((sum, w) => sum + w, 0),
		[columnWidths],
	);

	const gridTemplateColumns = useMemo(
		() => columnWidths.map((w) => `${w}px`).join(" "),
		[columnWidths],
	);

	const listHeight = useMemo(() => {
		if (fullHeight) {
			return itemCount * (typeof rowHeight === "number" ? rowHeight : 0);
		}

		if (typeof rowHeight === "number") {
			const contentHeight = itemCount * rowHeight;
			return Math.min(tabelaHeight, contentHeight || rowHeight);
		}

		return tabelaHeight;
	}, [itemCount, rowHeight, tabelaHeight, fullHeight]);

	const updateCentralize = useCallback(() => {
		if (!wrapperEl || !tableEl) return;

		const wrapperWidth = wrapperEl.offsetWidth;
		const tableWidth = tableEl.scrollWidth;
		setCentralizedByWidth(wrapperWidth > tableWidth ? true : !!centralized);
	}, [wrapperEl, tableEl, centralized]);

	useLayoutEffect(() => {
		if (!wrapperEl || !tableEl || isLoading) return;

		updateCentralize();

		const ro = new ResizeObserver(() => updateCentralize());
		ro.observe(wrapperEl);
		ro.observe(tableEl);

		return () => ro.disconnect();
	}, [wrapperEl, tableEl, isLoading, updateCentralize]);

	useEffect(() => {
		if (isLoading) return;
		if (!wrapperRef.current) return;

		const calculateHeight = () => {
			if (!wrapperRef.current) return;
			const rect = wrapperRef.current.getBoundingClientRect();
			const disponivel = window.innerHeight - rect.top - 140;
			setTabelaHeight(hasFooter ? disponivel : window.innerHeight - rect.top);
		};

		const id = requestAnimationFrame(calculateHeight);
		window.addEventListener("resize", calculateHeight);
		return () => {
			window.removeEventListener("resize", calculateHeight);
			cancelAnimationFrame(id);
		};
	}, [hasFooter, isLoading, itemCount, totalGridWidth]);

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		if (targetContentKey === contentKey) return;
		if (targetContentKey === "loading") {
			setContentKey("loading");
		} else {
			timeoutRef.current = setTimeout(
				() => setContentKey(targetContentKey),
				250,
			);
		}
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [targetContentKey]);

	return (
		<motion.div
			key={location.pathname}
			className="table-generica-container"
			ref={wrapperRef}
		>
			<AnimatePresence mode="wait">
				{contentKey === "empty" && (
					<motion.div
						key="empty"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
						className="modal-encontrar-veiculo-initial-container"
					>
						<span>Nenhum item foi localizado.</span>
						<Lottie
							animationData={Empty}
							loop
							autoplay
							style={{ height: 300 }}
						/>
					</motion.div>
				)}

				{contentKey === "loading" && <LoadingComponent key="loading" />}

				{contentKey === "loaded" && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
						className="outer-wrapper-generica"
						key="tabela-generica-loaded"
					>
						<div
							className={
								centralizedByWidth ? "table-generica-center" : "table-generica"
							}
							id="tableWrapper"
							ref={setWrapperEl}
						>
							<div className="wrapper-table-generica" ref={setTableEl}>
								<HeaderRow
									colunas={colunas}
									gridTemplateColumns={gridTemplateColumns}
									totalGridWidth={totalGridWidth}
									onHeaderClick={onHeaderClick}
								/>
								<List
									style={{
										width: totalGridWidth,
										height: listHeight,
										overflowX: "hidden",
										overflowY: "auto",
									}}
									rowComponent={GenericRow}
									rowCount={itemCount}
									overscanCount={10}
									rowHeight={rowHeight}
									rowProps={{
										dados,
										colunas,
										gridTemplateColumns,
										columnWidths,
										getRowClassName,
										getCellClassName,
										renderCell,
										onRowClick,
									}}
								/>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
}
